import React from 'react';
import cn from 'classnames';

import { Section, SectionTitle, AnchorArrow } from 'react-scale/components';
import CTA from 'react-scale/components/elements/button';

const Why = ({ id, overtitle, title, subtitle, texts, cta }) => (
  <div>
    <Section container={false} relative id={id}>
      <div className="container">
        <SectionTitle overtitle={overtitle} title={title} subtitle={subtitle} />
        <div className="grid items-center gap-8 md:grid-cols-2">
          <div>
            <h3 className="mb-8 text-2xl text-white lg:text-3xl">
              Technological &amp; Operational Excellence
            </h3>
            <div>
              {texts.map(text => {
                const cleanText = typeof text === 'string' ? text : text.text;
                return (
                  <p
                    key={cleanText}
                    className="text-base text-neutral-300 md:text-xl md:leading-relaxed first:mb-4 last:mb-8"
                  >
                    {cleanText}
                  </p>
                );
              })}
            </div>
            {cta && (
              <div className="mt-2 lg:mt-4">
                <CTA href="/sales" text="Book a Demo" type="secondary" />
              </div>
            )}
          </div>
          <div>
            <img
              className="rounded-lg shadow-2xl md:max-w-none"
              src="/static/img/website/solutions/content-language/metrics.svg"
              alt="Scale's Metrics"
            />
          </div>
        </div>
      </div>
    </Section>

    <Section bg="bg-neutral-900" container={false} relative>
      <div className="container">
        <ul className="grid gap-y-20 lg:gap-y-32">
          <Item
            title="Algorithmic Acquisition and Matching"
            text="Our ML-enabled system dynamically recruits and matches talent best suited to the task at hand. We continually analyze tasker expertise, historic performance, and cognitive abilities in 50+ languages across 70+ countries to ensure tasker-project fit. By finding the right people quickly, algorithmic matching allows us to optimize costs, mitigate bias, and ensure balanced semantic representation for your models."
          >
            <img
              className="shadow-2xl rounded-xl"
              src="/static/img/website/solutions/content-language/why/why01.svg"
              alt="Why-01"
            />
          </Item>
          <Item
            title="Data &amp; Quality Expertise"
            text="Our experience designing sophisticated pipelines and navigating subjective judgments ensures the highest quality data. We are partners to the world’s most ambitious AI projects and have a strong track record of advancing and delivering on complex labeling needs. Grappling with 200+ page guidelines, 10x surges, and unexpected edge cases? Leave it to us."
            reverse
          >
            <img
              className="w-full shadow-2xl rounded-xl"
              src="/static/img/website/solutions/content-language/why/why02.svg"
              alt="Why-02"
            />
          </Item>
          <Item
            title="Fast Feedback and Customized Training"
            text="Our customized training workflows and fast feedback loops provide deep insight into the nuances of your data. Scale University’s dynamic coursework, remedial learning, hybrid consensus and review pipelines, and active tasker community help continually refine taxonomies and tease out task ambiguities to ensure your data serves your needs."
          >
            <img
              className="shadow-2xl rounded-xl"
              src="/static/img/website/solutions/content-language/why/why03.svg"
              alt="Why-03"
            />
          </Item>
        </ul>
      </div>
    </Section>

    <Section id="ethical-ai" className="pt-40" bg="bg-neutral-800" relative>
      <div className="container grid items-center gap-8 md:grid-cols-2">
        <div>
          <h3 className="mb-4 text-3xl text-white font-aeonik">Ethical AI</h3>
          <p className="mb-6 text-base text-neutral-300 md:text-lg lg:text-xl lg:leading-relaxed">
            We are proud to pay rates at a living wage for all regions we have Taskers. We take into
            account the cost of living in our calculations, and all training is provided at no cost
            to our Taskers. We also leverage technology to ensure a diverse and inclusive Tasker
            community and to help our Taskers be more efficient.
          </p>
          <p className="text-lg">
            <AnchorArrow href="/blog/positive-externalities" text="Read Real Tasker Stories" />
          </p>
        </div>
        <div>
          <img
            className="shadow-xl rounded-xl md:max-w-none"
            src="/static/img/website/solutions/content-language/why/why-bottom.svg"
            alt="Scale's Ethical"
          />
        </div>
      </div>
    </Section>
  </div>
);

const Item = ({ title, text, reverse, children }) => (
  <li>
    <article className="grid items-center gap-4 md:grid-cols-2 lg:gap-14">
      <div className={reverse ? 'md:order-2' : 'md:order-1'}>
        <h3 className="mb-4 text-3xl text-white font-aeonik">{title}</h3>
        <p className="text-base text-neutral-300 md:text-lg lg:text-xl lg:leading-relaxed">
          {text}
        </p>
      </div>
      <div className={cn('relative', reverse ? 'md:order-1' : 'md:order-2')}>{children}</div>
    </article>
  </li>
);

export default Why;
